import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';

function MenuDropdown({ label, onSelect, selectedMenu, menus }) {
    const [items, setItems] = useState(menus || []);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        // Fetch menus only if not provided as props
        if (!menus || menus.length === 0) {
            const fetchMenus = async () => {
                try {
                    const apiToken = localStorage.getItem('accessToken');
                    const apiUrl = process.env.REACT_APP_API_URL;
                    const response = await axios.get(`${apiUrl}/menus`, {
                        headers: getHeaders(apiToken),
                    });
                    setItems([{ menu_id: null, name: 'No Menu Selected' }, ...response.data.data]);
                } catch (error) {
                    console.error('Error fetching menus', error);
                }
            };

            fetchMenus();
        } else {
            setItems([{ menu_id: null, name: 'No Menu Selected' }, ...menus]); // Include "None" option
        }
    }, [menus]);

    useEffect(() => {
        // Update selected item if `selectedMenu` changes
        if (selectedMenu) {
            const foundItem = items.find(item => item.menu_id === selectedMenu.menu_id || item.name === selectedMenu.name);
            setSelectedItem(foundItem);
        }
    }, [selectedMenu, items]);

    const toggleDropdown = () => setShowDropdown(!showDropdown);

    const handleSelectItem = (item) => {
        setSelectedItem(item);
        setShowDropdown(false);
        onSelect(item);
    };

    const renderedItems = useMemo(() => items, [items]); // Memoize menu items

    return (
        <div className="relative flex items-end space-x-4">
            <div className="flex-1">
                <label htmlFor="menu" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    {label}
                </label>
                <input
                    type="text"
                    id="menu"
                    name="menu"
                    value={selectedItem ? (selectedItem.menu_type ? `${selectedItem.name} (${selectedItem.menu_type})` : selectedItem.name) : ''}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    placeholder="Select Menu"
                    readOnly
                />
            </div>
            <button
                id="dropdownMenuButton"
                className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                type="button"
                onClick={toggleDropdown}
            >
                {'Select'}
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8l5 5 5-5" />
                </svg>
            </button>
            {showDropdown && (
                <div className="absolute z-10 mt-1 bg-white divide-y divide-gray-100 rounded shadow w-full dark:bg-gray-700" style={{ top: '100%', left: 0, maxHeight: '20rem', overflowY: 'auto' }}>
                    <ul className="py-1 text-sm text-gray-700 dark:text-gray-200" aria-labelledby="dropdownMenuButton">
                        {renderedItems.map((item) => (
                            <li key={item.menu_id}>
                                <a
                                    href="#"
                                    className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSelectItem(item);
                                    }}
                                >
                                    {item.menu_type ? `${item.name} (${item.menu_type})` : item.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

MenuDropdown.propTypes = {
    label: PropTypes.string.isRequired, // Add label as a required prop
    onSelect: PropTypes.func.isRequired,
    selectedMenu: PropTypes.shape({
        name: PropTypes.string,
        menu_id: PropTypes.string,
    }),
    menus: PropTypes.array, // Add menus as an optional prop
};

MenuDropdown.defaultProps = {
    menus: [], // Default menus to an empty array
};

export default MenuDropdown;
